import axios from 'axios'
import config from '../../variables/config'

/**
 * Whether a membership should be treated as already subscribed (blocks re-signup).
 * GoCardless ids start with SB; Stripe ids start with sub_.
 */
export function isActivelySubscribed (membership) {
  if (!membership || !membership.subscriptionId) return false
  const status = membership.status
  const activeStatuses = ['active', 'trialing', 'pending_customer_approval']
  if (!activeStatuses.includes(status)) return false

  if (membership.isStripe || String(membership.subscriptionId).startsWith('sub_')) {
    return status === 'active' || status === 'trialing'
  }

  // GoCardless
  return String(membership.subscriptionId).substring(0, 2) === 'SB'
}

function metaFlag (value) {
  return value === true || value === 'true' || value === '1'
}

/** Stripe `metadata.location`: one slug or several comma-separated. */
export function membershipProductLocationSlugs (locationMetadata) {
  return String(locationMetadata || '')
    .toLowerCase()
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

export function isPortalBranchProduct (product, branch) {
  const meta = (product && product.metadata) || {}
  if (!metaFlag(meta.forPortal)) return false
  const slug = String(branch || '').toLowerCase()
  if (!slug) return false
  return membershipProductLocationSlugs(meta.location).includes(slug)
}

function parseDescription (product) {
  const meta = (product && product.metadata) || {}
  const chunks = []
  if (product && product.description) chunks.push(String(product.description))
  if (meta.description) chunks.push(String(meta.description))
  if (meta.benefits) chunks.push(String(meta.benefits))

  const lines = chunks
    .join('\n')
    .split(/\r?\n|;/)
    .map((s) => s.replace(/^[-•]\s*/, '').trim())
    .filter(Boolean)

  return lines.length ? lines : [product.name || 'Membership']
}

/** Website-style label: "Child Premium Membership" → "Child Premium". */
export function displayMembershipName (productName) {
  const name = String(productName || '').trim()
  if (!name) return 'Membership'
  const stripped = name.split(/ membership/i)[0].trim()
  return stripped || name
}

/**
 * Map a Stripe catalog product onto the portal plan shape used by member/index.js.
 */
export function stripeProductToPortalPlan (product) {
  const meta = (product && product.metadata) || {}
  const name = (product && product.name) || 'Membership'
  const nameLower = name.toLowerCase()
  const isFamily = metaFlag(meta.isFamily) || nameLower.includes('family')
  const isSibling = metaFlag(meta.isSibling) || nameLower.includes('sibling')
  const isPremium =
    String(meta.tier || '').toLowerCase() === 'premium' || nameLower.includes('premium')

  let familyPlan = meta.familyPlanType || ''
  if (isFamily && !familyPlan) {
    if (nameLower.includes('multiple') || nameLower.includes('multi')) {
      familyPlan = 'multipleChildren'
    } else {
      familyPlan = 'twoAdultsTwoChildren'
    }
  }

  const minChildren = meta.minChildren
    ? parseInt(meta.minChildren, 10)
    : familyPlan === 'multipleChildren'
      ? 3
      : undefined
  const maxChildren = meta.maxChildren
    ? parseInt(meta.maxChildren, 10)
    : familyPlan === 'multipleChildren'
      ? 8
      : undefined

  return {
    name: displayMembershipName(name),
    stripeProduct: product,
    stripeProductName: name,
    priceKey: null,
    tier: isPremium ? 'Premium' : 'Standard',
    sibling: isSibling,
    family: isFamily,
    familyPlan: isFamily ? familyPlan : undefined,
    minChildren,
    maxChildren,
    description: parseDescription(product),
    prices: (product && product.prices) || []
  }
}

export function findStripePrice (plan, interval) {
  const prices = (plan && plan.prices) || []
  return prices.find((price) => price && price.recurring && price.recurring.interval === interval)
}

export function formatStripePayAmount (plan, memberType) {
  const interval = memberType === 1 ? 'month' : 'year'
  const price = findStripePrice(plan, interval)
  if (!price || typeof price.unit_amount !== 'number') return '—'
  const suffix = interval === 'month' ? ' per month' : ' per year'
  return (price.unit_amount / 100).toFixed(2) + suffix
}

/**
 * Southampton portal catalog: GET /stripe/price/membership?audience=portal
 * then keep products with metadata.forPortal=true and metadata.location including the branch.
 */
export async function fetchPortalStripeMemberships () {
  const { data } = await axios.get(
    config.serverURL + '/stripe/price/membership?audience=portal'
  )
  if (data.status !== 'success' || !data.products) {
    throw new Error('Could not load Stripe membership products')
  }

  const products = Array.isArray(data.products)
    ? data.products
    : Object.values(data.products)

  return products
    .filter((product) => isPortalBranchProduct(product, config.branch))
    .map(stripeProductToPortalPlan)
}

/**
 * Product name for subscriptionPay. Stripe-backed plans already carry the catalog name.
 */
export function resolveStripeProductName (plan) {
  if (plan && plan.stripeProductName) return plan.stripeProductName
  if (plan && plan.name) return plan.name
  throw new Error('Could not match a Stripe product for this plan')
}
